import { useState, useRef, useEffect } from 'react'
import Modal from '../../../components/Modal'
import { Button } from '../../../components/shadcn/Button'
import { ControlledInput } from '../../../components/Form/Controlled/ControlledInput'
import { ControlledSelect } from '../../../components/Form/Controlled/ControlledSelect'
import { useForm } from 'react-hook-form'
import { useBarcode, useBarcodePosition } from '../../../hooks/useBarcode'
import { useCore } from '../../../hooks'
import { useOrderDetailContext } from '../OrderDetailContext'
import { graphql, useFragment } from '../../../gql'
import { useBasicEntities } from '../../../hooks/useBasicEntities'

const ReturnAllModal_OrderItemFragment = graphql(`
  fragment ReturnAllModal_OrderItemFragment on OrderItem {
    id
    clearanceItems {
      id
      productDepots {
        depot {
          id
        }
      }
    }
  }
`)

export const ReturnAllModal_OrderFragment = graphql(`
  fragment ReturnAllModal_OrderFragment on Order {
    id
    company {
      id
    }
    items {
      ...ReturnAllModal_OrderItemFragment
    }
  }
`)

export interface ReturnAllModalProps {
  isOpen: boolean
  onClose: () => void
  positionDepotPicking?: PositionDepotPickingParams
  onPositionDepotPicked: (data: { position1: string; position2: string; position3: string; depotId: number }) => void
}

export interface ReturnAllHookProps {
  onPositionDepotPicked?: (data: { position1: string; position2: string; position3: string; depotId: number }) => void
}

export interface PositionDepotPickingParams {
  // You can add more fields if needed
}

export function useReturnAllModal({ onPositionDepotPicked }: ReturnAllHookProps = {}): {
  returnAllModalProps: ReturnAllModalProps
  open: (
    onPositionDepotPicked?: (data: {
      position1: string
      position2: string
      position3: string
      depotId: number
    }) => void,
  ) => void
  close: () => void
} {
  const [isOpen, setIsOpen] = useState(false)
  const [positionDepotPicking, setPositionDepotPicking] = useState<PositionDepotPickingParams | undefined>(undefined)

  const callbackRef = useRef<
    ((data: { position1: string; position2: string; position3: string; depotId: number }) => void) | undefined
  >(undefined)
  const handlePositionDepotPicked = (data: {
    position1: string
    position2: string
    position3: string
    depotId: number
  }) => {
    callbackRef.current?.(data)
    onPositionDepotPicked?.(data)
    setIsOpen(false)
    setPositionDepotPicking(undefined)
  }

  return {
    returnAllModalProps: {
      isOpen,
      onClose: () => setIsOpen(false),
      positionDepotPicking,
      onPositionDepotPicked: handlePositionDepotPicked,
    },
    open: (
      onPositionDepotPicked?: (data: {
        position1: string
        position2: string
        position3: string
        depotId: number
      }) => void,
    ) => {
      setIsOpen(true)
      setPositionDepotPicking({})
      if (onPositionDepotPicked) {
        callbackRef.current = onPositionDepotPicked
      }
    },
    close: () => setIsOpen(false),
  }
}

export default function ReturnAllModal({
  isOpen,
  onClose,
  positionDepotPicking,
  onPositionDepotPicked,
}: ReturnAllModalProps) {
  const context = useOrderDetailContext()
  const order = useFragment(ReturnAllModal_OrderFragment, context.order)

  // TODO: Maybe simplify this somehow?
  const itemsWithFragments = order.items.map(i => useFragment(ReturnAllModal_OrderItemFragment, i))
  const everyItemHasSameDepot = itemsWithFragments
    .map(item => {
      if (!Array.isArray(item.clearanceItems) || item.clearanceItems.length <= 0) {
        return null
      }
      return item.clearanceItems[0].productDepots[0].depot?.id
    })
    .every((depotId, _, all) => depotId === all[0])

  const defaultDepotId =
    everyItemHasSameDepot && itemsWithFragments.length > 0
      ? itemsWithFragments[0]?.clearanceItems[0]?.productDepots[0].depot?.id
      : ''

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      position1: '',
      position2: '',
      position3: '',
      depotId: defaultDepotId || '',
    },
  })

  const { depots } = useBasicEntities()
  const depotOptions = [
    { label: 'Vyberte sklad', value: null },
    ...depots.filter(d => d.company.id === order.company.id).map(d => ({ label: d.name, value: d.id.toString() })),
  ]

  const handleContinue = handleSubmit(values => {
    if (values.depotId) {
      onPositionDepotPicked({
        position1: values.position1,
        position2: values.position2,
        position3: values.position3,
        depotId: parseInt(values.depotId.toString()),
      })
    }
  })

  // Barcode handler for position scanning
  useBarcodePosition(
    position => {
      if (isOpen) {
        setValue('position1', position.position1)
        setValue('position2', position.position2)
        setValue('position3', position.position3)
      }
    },
    { autoStart: true },
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Vrátit všechny položky" closable={true}>
      <div className="text-sm text-gray-500 mb-4">
        Všechny položky budou vráceny na stejnou pozici. Tady se prosím vyberte, na kterou pozici budou vráceny. Pokud
        máte zboží z více skladů, vyberte sklad, na který budou vráceny.
      </div>
      <form onSubmit={handleContinue}>
        <div className="flex flex-col gap-4 mb-4">
          <div className="grid grid-cols-3 gap-4">
            <ControlledInput
              control={control}
              name="position1"
              type="text"
              label="Pozice 1"
              rules={{ minLength: 0, maxLength: 3, required: false }}
            />
            <ControlledInput
              control={control}
              name="position2"
              type="text"
              label="Pozice 2"
              rules={{ minLength: 0, maxLength: 3, required: false }}
            />
            <ControlledInput
              control={control}
              name="position3"
              type="text"
              label="Pozice 3"
              rules={{ minLength: 0, maxLength: 3, required: false }}
            />
          </div>
          <div>
            <ControlledSelect
              control={control}
              name="depotId"
              label="Depot"
              options={depotOptions}
              rules={{ required: true }}
              className="w-full"
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button type="submit" className="w-44" onClick={handleContinue} disabled={!isValid}>
            Continue
          </Button>
        </div>
      </form>
    </Modal>
  )
}
